import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function InviteAccept() {
  const { token } = useParams();
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [invite, setInvite] = useState(null); // null=loading, false=invalid
  const [form, setForm] = useState({ password: "", confirm: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get(`/invite/${token}`)
      .then((r) => setInvite(r.data))
      .catch((err) => { setInvite(false); toast.error(formatApiErrorDetail(err.response?.data?.detail)); });
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm) return toast.error("Passwords do not match");
    if (form.password.length < 6) return toast.error("Password must be at least 6 characters");
    setLoading(true);
    try {
      const { data } = await api.post(`/invite/${token}/accept`, { password: form.password });
      setUser(data);
      toast.success("Welcome to PayFlex!");
      navigate("/app");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between bg-slate-900 p-12 text-white grid-bg">
        <Logo light />
        <div>
          <h2 className="font-display text-4xl font-extrabold leading-tight">Set up your payroll portal.</h2>
          <p className="text-slate-400 mt-4 max-w-md">Choose a password to activate your account and start managing your team's payroll.</p>
        </div>
        <span className="text-slate-600 text-sm">© 2026 PayFlex Portal</span>
      </div>

      <div className="flex items-center justify-center p-6 bg-white">
        <div className="w-full max-w-sm animate-fade-up">
          <div className="lg:hidden mb-8"><Logo /></div>
          {invite === null && <p className="text-slate-400" data-testid="invite-loading">Checking your invite…</p>}
          {invite === false && (
            <div data-testid="invite-invalid">
              <h1 className="font-display text-2xl font-extrabold">Invite not valid</h1>
              <p className="text-slate-500 text-sm mt-2">This link is invalid, expired, or already used. Please ask your administrator for a new invite.</p>
              <Button onClick={() => navigate("/login")} className="mt-6 bg-sky-600 hover:bg-sky-700 rounded-xl">Go to sign in</Button>
            </div>
          )}
          {invite && (
            <form onSubmit={submit} data-testid="invite-form">
              <h1 className="font-display text-2xl font-extrabold">Activate your account</h1>
              <p className="text-slate-500 text-sm mt-1 mb-6">
                {invite.company_name} · <span className="font-medium text-slate-700">{invite.email}</span>
              </p>
              <div className="space-y-4">
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Create password</Label>
                  <Input data-testid="invite-password-input" type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="At least 6 characters" className="mt-1.5 h-11" />
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Confirm password</Label>
                  <Input data-testid="invite-confirm-input" type="password" required value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} className="mt-1.5 h-11" />
                </div>
              </div>
              <Button type="submit" disabled={loading} data-testid="invite-submit-button" className="w-full mt-6 h-11 bg-sky-600 hover:bg-sky-700 rounded-xl font-semibold">{loading ? "Activating…" : "Activate & sign in"}</Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
